import { CloseButton, Group } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { Show } from '../components/control-flow/Show';
import type {
    Controlled_ValidatedInputProps,
    ValueOrNullableValue,
} from '../components/inputs/controlled/types';
import { LabelNode } from '../components/inputs/getLabelNode';
import { getColorMantine } from '../components/inputs/text-fields/types';
import { ValidationEndAdornment } from '../components/ValidationEndAdornment';
import type { OptionalProps } from '../utils/optionalProps';

export const useCommonInputProps = <N extends boolean, T>({
    endingIcon,
    severity,
    helperText,
    label,
    labelInfo,
    placeholder,
    readOnly,
    nullable,
    onChange,
    value,
    nullableInReadOnly,
    clearable = nullable,
    defaultValue = null as ValueOrNullableValue<N, T>,
}: Controlled_ValidatedInputProps<N, T> & {
    placeholder: string | undefined;
    readOnly: boolean;
} & OptionalProps<{
        clearable: boolean;
        defaultValue: ValueOrNullableValue<N, T>;
    }>) => {
    const { ref, width } = useElementSize();

    return {
        label: label && (
            <LabelNode
                label={label}
                labelInfo={labelInfo}
            />
        ),
        placeholder: placeholder ?? 'not defined',
        inputStyles: {
            borderColor: getColorMantine(severity),
            backgroundColor:
                readOnly ? 'var(--mantine-color-read-only-input)' : undefined,
        },
        rightSectionWidth: width,
        rightSection: (
            <Group
                ref={ref}
                gap={0}
                align={'center'}
                wrap={'nowrap'}
                pr={severity && helperText ? 4 : undefined}
            >
                <Show
                    whenAll={[
                        onChange,
                        clearable,
                        !readOnly || nullableInReadOnly,
                        value !== defaultValue,
                    ]}
                >
                    {([handleChange]) => (
                        <CloseButton
                            size="md"
                            variant="transparent"
                            onClick={() => handleChange(defaultValue)}
                        />
                    )}
                </Show>
                {endingIcon}
                <ValidationEndAdornment
                    severity={severity}
                    helperText={helperText}
                />
            </Group>
        ),
    };
};
